import { useContext, useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, Fragment, memo } from 'react';
import { AppState } from '../context/general';
import { goTo, RouterContext, useLocation } from 't-lib-ui/router';
import { A } from 't-lib-ui/widgets';
import ClockIcon from './ClockIcon';
import PrivateFeedIcon from './PrivateFeedIcon';
import MailIcon from './MailIcon';
import FeedIssueIcon from './FeedIssueIcon';
import FeedIssuePopup from './FeedIssuePopup';
import FeedLoadError from './FeedLoadError';
import { useEngineState } from '../utils/useEngineState';
import { hasFeedIssue } from '../utils/feedIssue';
import ChevronRightIcon from './ChevronRightIcon';
import ManageFeedsIcon from './ManageFeedsIcon';
import HomeIcon from './HomeIcon';
import DownreadLogo from './DownreadLogo';
import MoreDotsIcon from './MoreDotsIcon';
import NewPostsIcon from './NewPostsIcon';
import PlusIcon from './PlusIcon';
import FilterIcon from './FilterIcon';
import SettingsIcon from './SettingsIcon';
import StarIcon from './StarIcon';
import UserIcon from './UserIcon';
import DeleteFolderModal from './DeleteFolderModal';
import { isOwner, isSuperuser } from '../schema-copy/capabilities';
import { getPlanLimits } from '../schema-copy/userPlans';
import { Badge } from 't-lib-ui/widgets';

import { useConfirmationModal, Popup } from 't-lib-ui/structure';
import { RestUserFeed, RestFolderUpdate } from '../schema-copy/api-schema';
import { formatUnreadCount } from '../utils/formatting';
import { usePreferences } from '../connector/preferences';
import { LocalNotice } from '../connector/feed-engine';
import { useLeftSidebarFocus } from './LeftSidebarFocus';
import { menuKeyDown, menuBlur } from './menuUtils';
import FeedInfoPopup from './FeedInfoPopup';
import NewsletterInfoPopup from './NewsletterInfoPopup';
import RenameFeedPopup from './RenameFeedPopup';
import { useShare } from './SharePopup';
import { ShowMeWhereGuide, useShowMeWhere } from './ShowMeWhere';
import { FocusText } from 't-lib-ui/widgets';
import { useMenuPosition } from '../utils/useMenuPosition';
import { Spinner } from 't-lib-ui/widgets';
import { adminNotificationEngine } from '../connector/admin-notification-engine';
import FlagIcon from './FlagIcon';
import FeedIcon from './FeedIcon';
import PauseIcon from './PauseIcon';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { isFolderOpen, pickFoldersToKeepOpen, updateFolderOpenOrder, MAX_FOLDERS_OPEN_ON_SHOW, MAX_FOLDERS } from './sidebarFolders';
import './LeftSidebar.css';

const FEEDS_HEADER_HIT_MARGIN = 8;

interface LeftSidebarProps {
  selectedFeed?: string;
  onSelectFeed?: (feedId: string, isReselect?: boolean) => void;
  onCollapse?: () => void;
  onMenuClick?: (options?: { isReselect?: boolean }) => void;
  isMobile?: boolean;
  // True when the sidebar is an overlay drawer (mobile/narrow screens) rather than a permanent column.
  isDrawer?: boolean;
}

// Set by the first sidebar mount after a page load. Later desktop mounts (plain page navigation)
// must not re-collapse folders the user just opened.
let didTrimFoldersThisPageLoad = false;

// Component to render unread indicator as count, dot, spinner, or a paused feed's pause icon
// `plus` appends a '+' to the count — used on folder badges where the number reflects only the
// tracked ('u') feeds but untracked ('a') feeds in the folder also have unread posts (e.g. '8+').
const UnreadIndicator: React.FC<{ count: number; showCounts: boolean; loading?: boolean; plus?: boolean; paused?: boolean }> = ({ count, showCounts, loading, plus, paused }) => {
  if (paused)
    return <span className="unreadPaused" title="Paused"><PauseIcon size={10} /></span>;
  if (loading && !count)
    return <Spinner spinning className="unreadCount" />;
  // Nothing unread: never show a "0", just reserve the slot to keep the column aligned.
  if (!count)
    return <span className="unreadPlaceholder" />;
  if (!showCounts)
    return <span className="unreadDot" />;
  const label = formatUnreadCount(count);
  return <span className="unreadCount">{plus && !label.endsWith('+') ? label + '+' : label}</span>;
};

// One sidebar feed row. Memoized (all callback props must be reference-stable) so an
// unread tick or filter keystroke only re-renders the affected row, not all ~n feeds.
interface FeedRowProps {
  feed: RestUserFeed;
  unread: number;
  showCounts: boolean;
  paused: boolean;
  loading: boolean;
  selected: boolean;
  dragging: boolean;
  /** Context menu dropdown; non-null only for the row whose menu is open. */
  menu: React.ReactNode;
  onFeedClick: (feedId: string) => void;
  onContextMenuClick: (e: React.MouseEvent, type: 'feed' | 'folder' | 'special', id: string) => void;
  onContextMenuMouseDown: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent, feedId: string) => void;
  onDragEnd: () => void;
  onNewsletterInfo: (feedId: string) => void;
  onPrivateFeedInfo: () => void;
  onIssueInfo: (feedId: string) => void;
}

const FeedRow = memo(({ feed, unread, showCounts, paused, loading, selected, dragging, menu, onFeedClick, onContextMenuClick, onContextMenuMouseDown, onDragStart, onDragEnd, onNewsletterInfo, onPrivateFeedInfo, onIssueInfo }: FeedRowProps) => (
  <div
    className={`feedItem flowFocussable ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
    tabIndex={0}
    draggable
    onDragStart={(e) => onDragStart(e, feed.id)}
    onDragEnd={onDragEnd}
    onClick={() => onFeedClick(feed.id)}
    onContextMenu={(e) => { e.preventDefault(); onContextMenuClick(e, 'feed', feed.id); }}
  >
    <div className="feedItemContent">
      <FeedIcon className="feedIcon" src={getFeedIconUrl(feed, 32)} size={16} circular={feed.circularIcon} fallback={<span className="feedIcon">📰</span>} />
      <span className="feedName">{feed.shortTitle || feed.title}</span>
      {feed.feedStatus == 'NEWSLETTER' && (
        <button id={`sbNlBadge-${feed.id}`} className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={(e) => { e.stopPropagation(); onNewsletterInfo(feed.id); }} title="Newsletter feed">
          <MailIcon size={12} color="currentColor" />
        </button>
      )}
      {feed.feedStatus == 'PRIVATE' && (
        <button className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={(e) => { e.stopPropagation(); onPrivateFeedInfo(); }} title="Private feed">
          <PrivateFeedIcon size={12} color="currentColor" />
        </button>
      )}
      {hasFeedIssue(feed) && (
        <button id={`sbIssueBadge-${feed.id}`} className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={(e) => { e.stopPropagation(); onIssueInfo(feed.id); }} title="This feed has a problem">
          <FeedIssueIcon size={12} color="var(--error-color)" />
        </button>
      )}
    </div>
    <div className="feedMenuContainer">
      <button
        className="contextMenuButton flowFocussable sidewayFocus"
        onMouseDown={onContextMenuMouseDown}
        onClick={(e) => onContextMenuClick(e, 'feed', feed.id)}
        title="Feed options"
      >
        <MoreDotsIcon size={16} />
      </button>
      {menu}
    </div>
    <UnreadIndicator count={unread} showCounts={showCounts} paused={paused} loading={loading} />
  </div>
));

let savedScrollTop = 0;

const LeftSidebar: React.FC<LeftSidebarProps> = ({ selectedFeed, onSelectFeed, onCollapse, onMenuClick, isMobile, isDrawer }) => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const location = useLocation();
  // On /a/new/<folder> the reader shows only that folder's unread posts; selectedFeed is
  // 'all-items' in that case, so we read the folder off the URL to highlight the folder's
  // virtual "Read all" row (and keep the plain "Unread posts" row unselected).
  const activeNewFolder = useMemo(() => {
    const m = location.pathName.match(/^\/a\/new\/(.+)$/);
    if (!m) return undefined;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }, [location.pathName]);
  const isAdmin = isSuperuser(appState.state.authData?.selectedMembership?.capabilities);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [preferences, updatePreferences] = usePreferences();
  // Per-feed: show numeric unread counts only for "read every post" feeds; browse-only
  // ('a') feeds show just a dot. Special/aggregate rows always show counts.
  const feedShowsCounts = (feedId: string) => appState.feedEngine.getShowRead(feedId) == 'u';
  const showAllFeeds = preferences.showAllFeeds == 'yes';
  useLeftSidebarFocus(sidebarRef);
  const { share, sharePopup } = useShare();
  
  const [todosCount, setTodosCount] = useState(() => adminNotificationEngine.getCount());
  const [inactiveFeedsCount, setInactiveFeedsCount] = useState(() => adminNotificationEngine.getInactiveCount());
  const [reportsCount, setReportsCount] = useState(() => adminNotificationEngine.getReportsCount());
  const [needsAdminSecret, setNeedsAdminSecret] = useState(() => adminNotificationEngine.getNeedsMagic());
  const [yellowfeedsCount, setYellowfeedsCount] = useState(() => adminNotificationEngine.getYellowfeedsCount());

  // Subscribe to admin notification counts (Feed TODOs + recently-inactive discover feeds + content reports)
  useEffect(() => {
    const unsubTodos = adminNotificationEngine.onCountChange(setTodosCount);
    const unsubInactive = adminNotificationEngine.onInactiveCountChange(setInactiveFeedsCount);
    const unsubReports = adminNotificationEngine.onReportsCountChange(setReportsCount);
    const unsubMagic = adminNotificationEngine.onNeedsMagicChange(setNeedsAdminSecret);
    const unsubYellowfeeds = adminNotificationEngine.onYellowfeedsCountChange(setYellowfeedsCount);
    return () => { unsubTodos(); unsubInactive(); unsubReports(); unsubMagic(); unsubYellowfeeds(); };
  }, []);

  // Restore scroll position on mount, save continuously via scroll listener
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    el.scrollTop = savedScrollTop;
    const onScroll = () => { savedScrollTop = el.scrollTop; };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Read selectedFeed through a ref so the callback stays reference-stable for the
  // memoized FeedRow (a selection change would otherwise re-render every row).
  const selectedFeedRef = useRef(selectedFeed);
  selectedFeedRef.current = selectedFeed;
  const handleFeedClick = useCallback((feedId: string) => {
    const isReselect = selectedFeedRef.current == feedId;
    onSelectFeed?.(feedId, isReselect);
    onMenuClick?.({ isReselect });
  }, [onSelectFeed, onMenuClick]);

  const handleAddFeedClick = () => {
    goTo(routerCtx, '/a/add');
    onMenuClick?.();
  };

  const handleAdminClick = () => {
    goTo(routerCtx, '/a/admin/feeds');
    onMenuClick?.();
  };

  const handleFeedTodosClick = () => {
    goTo(routerCtx, '/a/admin/process/next');
    onMenuClick?.();
  };

  const handleDebugFeedClick = () => {
    goTo(routerCtx, '/a/admin/feed/debug');
    onMenuClick?.();
  };

  const handleDiscoverAdminClick = () => {
    goTo(routerCtx, '/a/admin/discover');
    onMenuClick?.();
  };

  const handleInactiveFeedsAdminClick = () => {
    goTo(routerCtx, '/a/admin/inactive-feeds');
    onMenuClick?.();
  };

  const handleReportsAdminClick = () => {
    goTo(routerCtx, '/a/admin/reports');
    onMenuClick?.();
  };

  // Yellowfeeds' review queue is its publisher console's admin page (a separate app, so a new tab).
  const handleYellowfeedsAdminClick = () => {
    window.open('https://rssdirectory.org/publishers/admin/list', '_blank', 'noopener');
    onMenuClick?.();
  };

  const handleRetentionAdminClick = () => {
    goTo(routerCtx, '/a/admin/retention');
    onMenuClick?.();
  };

  const handleImagesAdminClick = () => {
    goTo(routerCtx, '/a/admin/images');
    onMenuClick?.();
  };

  const handleCatalogsAdminClick = () => {
    goTo(routerCtx, '/a/admin/catalogs');
    onMenuClick?.();
  };

  const handleUsersAdminClick = () => {
    goTo(routerCtx, '/a/admin/users');
    onMenuClick?.();
  };

  const handleInvitationsAdminClick = () => {
    goTo(routerCtx, '/a/admin/invitations');
    onMenuClick?.();
  };

  const handleAccountClick = () => {
    goTo(routerCtx, '/a/account');
    onMenuClick?.();
  };

  const handlePreferencesClick = () => {
    goTo(routerCtx, '/a/preferences');
    onMenuClick?.();
  };

  // Load initial expansion state from localStorage
  const loadExpansionState = () => {
    try {
      const saved = localStorage.getItem('sidebar-expansion');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load sidebar expansion state:', e);
    }
    return null;
  };

  // Lazy useState: parse localStorage once per mount, not on every render
  const [savedState] = useState(loadExpansionState);

  const [isHomeExpanded, setIsHomeExpanded] = useState(savedState?.sections?.home ?? true);
  const [isAdminExpanded, setIsAdminExpanded] = useState(savedState?.sections?.admin ?? true);
  const [isFeedsExpanded, setIsFeedsExpanded] = useState(savedState?.sections?.feeds ?? true);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>(savedState?.folders ?? {});
  // Folder names in the order they were expanded, most recent last.
  const [folderOpenOrder, setFolderOpenOrder] = useState<string[]>(savedState?.folderOrder ?? []);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  // Drag & drop state. beforeFeedId anchors the drop ("insert before this feed",
  // null = end of group) so hidden rows (unread mode, filter) can't shift indices.
  const [draggedFeedId, setDraggedFeedId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ folder: string | null; beforeFeedId: string | null } | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ type: 'feed' | 'folder' | 'special'; id: string } | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [showFeedsSectionMenu, setShowFeedsSectionMenu] = useState(false);
  const [filterActive, setFilterActive] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  // Set when leaving filter mode so focus returns to the filter button (where the
  // user left off) instead of vanishing once the input/clear button unmount.
  const refocusFilterButtonRef = useRef(false);
  const feedsSectionMenuRef = useRef<HTMLDivElement>(null);
  const feedsSectionMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const contextMenuTriggerRef = useRef<HTMLElement | null>(null);
  const contextMenuDropdownRef = useRef<HTMLDivElement>(null);
  const feedsSectionMenuDropdownRef = useRef<HTMLDivElement>(null);
  const feedsHeaderRef = useRef<HTMLHeadingElement>(null);
  const showAllWhereRef = useRef<HTMLSpanElement>(null);
  const { trigger: triggerShowAllWhere, guide: showAllWhereGuide, flashClassName: showAllWhereFlashClass } = useShowMeWhere(showAllWhereRef, feedsHeaderRef);

  // Mouse clicks only toggle Unread/All feeds on the title (plus FEEDS_HEADER_HIT_MARGIN px around it),
  // and never on the half of the gap closer to the + button, so aiming for + can't switch the view.
  // Keyboard activation (synthetic click, detail 0) always toggles.
  const handleFeedsHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const title = feedsHeaderRef.current;
    if (e.detail && title) {
      const r = title.getBoundingClientRect();
      const addButton = e.currentTarget.querySelector('.addFeedButton');
      const maxX = Math.min(r.right + FEEDS_HEADER_HIT_MARGIN, addButton ? (r.right + addButton.getBoundingClientRect().left) / 2 : Infinity);
      if (e.clientX < r.left - FEEDS_HEADER_HIT_MARGIN || e.clientX > maxX
        || e.clientY < r.top - FEEDS_HEADER_HIT_MARGIN || e.clientY > r.bottom + FEEDS_HEADER_HIT_MARGIN)
        return;
    }
    updatePreferences({ showAllFeeds: showAllFeeds ? 'unread' : 'yes' });
  };

  // Feed info popup state
  const [infoFeedId, setInfoFeedId] = useState<string | null>(null);

  // Rename feed popup state
  const [renameFeedId, setRenameFeedId] = useState<string | null>(null);

  // Private feed info popup state
  const [privateFeedInfoOpen, setPrivateFeedInfoOpen] = useState(false);
  const openPrivateFeedInfo = useCallback(() => setPrivateFeedInfoOpen(true), []);
  const [newsletterInfoId, setNewsletterInfoId] = useState<string | null>(null);
  const [issueFeedId, setIssueFeedId] = useState<string | null>(null);

  // Delete folder modal state
  const [deleteFolderModal, setDeleteFolderModal] = useState<{ folderName: string; feedCount: number } | null>(null);

  // Rename folder state
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const renameFolderInputRef = useRef<HTMLInputElement>(null);
  // Folder name to refocus once the rename input unmounts (keyboard flow only), so
  // focus returns to the folder header instead of vanishing. Set to the *final*
  // name since Enter may have renamed the folder.
  const refocusFolderRef = useRef<string | null>(null);
  // Distinguishes Enter (keyboard) blur from a mouse-click-away blur, so we only
  // steal focus back to the folder when the user committed via keyboard.
  const renameViaKeyboardRef = useRef(false);

  const handleContextMenuMouseDown = useCallback((e: React.MouseEvent) => {
    // Prevent this mousedown from triggering ContextMenu's outside click handler
    e.stopPropagation();
  }, []);

  const closeContextMenu = () => {
    setContextMenu(null);
    setOpenSubmenu(null);
  };

  const handleContextMenuClick = useCallback((e: React.MouseEvent, type: 'feed' | 'folder' | 'special', id: string) => {
    e.stopPropagation();
    contextMenuTriggerRef.current = e.currentTarget as HTMLElement;
    // Clicking the button whose menu is already open closes it again
    setContextMenu(prev => prev?.type == type && prev.id == id ? null : { type, id });
    setOpenSubmenu(null);
  }, []);

  const handleFeedsSectionMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFeedsSectionMenu(!showFeedsSectionMenu);
  };

  const handleFilterButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFilterText('');
    setFilterActive(true);
  };

  // Leave filter mode only when the field is empty (a non-empty filter stays put
  // on blur so the list keeps showing the matches).
  const handleFilterBlur = () => {
    if (!filterText.trim()) setFilterActive(false);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterActive(false);
    refocusFilterButtonRef.current = true;
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Escape') clearFilter();
  };

  // Focus the filter input when entering filter mode; when leaving via clear,
  // return focus to the filter button so the user stays where they left off.
  useEffect(() => {
    if (filterActive) filterInputRef.current?.focus();
    else if (refocusFilterButtonRef.current) {
      refocusFilterButtonRef.current = false;
      filterButtonRef.current?.focus();
    }
  }, [filterActive]);

  const handleUnfollow = async (feedId: string) => {
    await appState.feedEngine.unsubscribe(feedId);
  };

  // Deleting a newsletter feed is irreversible (its posts are wiped and the sender is
  // unsubscribed), so confirm first — unlike unfollowing a normal feed.
  const showDeleteNewsletterConfirmation = useConfirmationModal<string>({
    title: 'Delete newsletter feed',
    confirmContent: 'Delete',
    dismissContent: 'Cancel',
    sidewayFocus: true,
    children: 'This newsletter inbox and all its posts will be permanently deleted and cannot be restored. The newsletter will be unsubscribed.',
    onConfirm: (feedId: string) => handleUnfollow(feedId),
  });

  const handleDeleteFolder = async (folderName: string) => {
    const folderFeeds = folders[folderName] ?? [];
    
    if (folderFeeds.length === 0) {
      // Empty folder - just delete it
      await appState.feedEngine.deleteFolder(folderName, false);
    } else {
      // Non-empty folder - show confirmation modal
      setDeleteFolderModal({ folderName, feedCount: folderFeeds.length });
    }
  };

  const handleDeleteFolderConfirm = async (deleteFeeds: boolean) => {
    if (!deleteFolderModal) return;
    const { folderName } = deleteFolderModal;
    setDeleteFolderModal(null);
    await appState.feedEngine.deleteFolder(folderName, deleteFeeds);
  };

  const handleContextMenuItemClick = async (action: string, submenuValue?: string) => {
    if (!contextMenu) return;
    const { type, id } = contextMenu;
    const trigger = contextMenuTriggerRef.current;
    closeContextMenu();
    trigger?.focus();

    if (type === 'feed') {
      switch (action) {
        case 'mark-read':
          appState.feedEngine.markAllRead(id);
          // If this feed is currently selected, trigger reload
          if (selectedFeed === id) {
            onSelectFeed?.(id);
          }
          break;
        case 'unfollow':
          if (feedById.get(id)?.feedStatus == 'NEWSLETTER') showDeleteNewsletterConfirmation({ onConfirmArg: id });
          else await handleUnfollow(id);
          break;
        case 'move':
          if (submenuValue !== undefined) {
            // submenuValue is the folder name, or '' for unfiled
            const targetFolder = submenuValue === '' ? null : submenuValue;
            await appState.feedEngine.moveFeed(id, targetFolder);
          }
          break;
        case 'share': {
          const feed = feedById.get(id);
          if (feed)
            share(feed.shortTitle || feed.title, `https://feeds.downread.com/a/feed/${feed.friendlyId || feed.id}`);
          break;
        }
        case 'info':
          setInfoFeedId(id);
          break;
        case 'rename':
          setRenameFeedId(id);
          break;
        case 'admin':
          goTo(routerCtx, `/a/admin/feed/${id}`);
          break;
        case 'create-folder': {
          // Generate unique folder name
          const currentFolders = appState.feedEngine.getFolders();
          let newFolderName = 'Untitled';
          let counter = 2;
          while (newFolderName in currentFolders) {
            newFolderName = `Untitled ${counter}`;
            counter++;
          }
          // Create folder and move feed into it
          const success = await appState.feedEngine.addEmptyFolder(newFolderName);
          if (success) {
            await appState.feedEngine.moveFeed(id, newFolderName, 0);
            // Start renaming the folder
            setRenamingFolder(newFolderName);
            setRenameFolderValue(newFolderName);
          }
          break;
        }
        // TODO: Implement other feed actions
        default:
          console.log(`Action: ${action} on feed ${id}`);
      }
    } else if (type === 'folder') {
      switch (action) {
        case 'mark-read': {
          const folderFeeds = folders[id] ?? [];
          for (const feedId of folderFeeds) {
            appState.feedEngine.markAllRead(feedId);
          }
          // If any feed in this folder is currently selected, trigger reload
          if (selectedFeed && folderFeeds.includes(selectedFeed)) {
            onSelectFeed?.(selectedFeed);
          }
          break;
        }
        case 'delete':
          await handleDeleteFolder(id);
          break;
        case 'rename':
          setRenamingFolder(id);
          setRenameFolderValue(id);
          break;
        // TODO: Implement other folder actions
        default:
          console.log(`Action: ${action} on folder ${id}`);
      }
    } else if (id == 'all-items') {
      switch (action) {
        case 'mark-read':
          showMarkAllReadConfirmation();
          break;
      }
    } else {
      const listId = id as 'starred' | 'read-later';
      const rawEntries = listId == 'starred'
        ? appState.feedEngine.getStarredEntries()
        : appState.feedEngine.getReadLaterEntries();
      const entries = rawEntries.map(entry => {
        const dashIdx = entry.lastIndexOf('-');
        return [entry.substring(0, dashIdx), parseInt(entry.substring(dashIdx + 1), 10)] as [string, number];
      });

      switch (action) {
        case 'mark-read':
          for (const [feedId, postIndex] of entries)
            appState.feedEngine.markPostRead(feedId, postIndex);
          break;
        case 'remove-all':
          for (const [feedId, postIndex] of entries) {
            if (listId == 'starred') appState.feedEngine.unstarPost(feedId, postIndex);
            else appState.feedEngine.unmarkReadLater(feedId, postIndex);
          }
          break;
        default:
          console.log(`Action: ${action} on special list ${listId}`);
      }

      if (selectedFeed == listId) {
        onSelectFeed?.(listId);
      }
    }
  };

  const handleFeedsSectionMenuItemClick = async (action: string) => {
    setShowFeedsSectionMenu(false);
    const trigger = feedsSectionMenuRef.current?.querySelector('.contextMenuButton') as HTMLElement | null;
    trigger?.focus();

    switch (action) {
      case 'add-feed':
        handleAddFeedClick();
        break;
      case 'create-folder-section':
        handleAddFolderClick();
        break;
      case 'toggle-show-all-feeds':
        updatePreferences({ showAllFeeds: showAllFeeds ? 'unread' : 'yes' });
        break;
      default:
        console.log(`Action: ${action} on feeds section`);
    }
  };

  // Real feed data from engine (live-subscribed: a LOADING->ERROR flip must re-render,
  // it fires no feeds-change event)
  const feedEngineState = useEngineState(appState.feedEngine);
  const feedsLoading = feedEngineState == 'OFF' || feedEngineState == 'LOADING';
  const [feeds, setFeeds] = useState<RestUserFeed[]>(() => appState.feedEngine.getFeeds());
  const [folders, setFolders] = useState<RestFolderUpdate>(() => appState.feedEngine.getFolders());
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [starredCount, setStarredCount] = useState(() => appState.feedEngine.getStarredCount());
  const [readLaterCount, setReadLaterCount] = useState(() => appState.feedEngine.getReadLaterCount());
  const [notice, setNotice] = useState<LocalNotice | null>(() => appState.feedEngine.getNotice());
  const [noticeTimeLabel, setNoticeTimeLabel] = useState('');
  const [newPostsIconAnimTick, setNewPostsIconAnimTick] = useState(0);
  const [starIconAnimTick, setStarIconAnimTick] = useState(0);
  const [readLaterIconAnimTick, setReadLaterIconAnimTick] = useState(0);

  // Generate folder submenu items for move-to-folder
  const getFolderSubmenuItems = (currentFeedId: string): { label: string; value: string }[] => {
    // Find current folder of this feed
    let currentFolder: string | null = null;
    for (const [folderName, feedIds] of Object.entries(folders)) {
      if (feedIds.includes(currentFeedId)) {
        currentFolder = folderName === '' ? null : folderName;
        break;
      }
    }

    const items: { label: string; value: string }[] = [];

    // Add "No folder" option if feed is in a folder
    if (currentFolder !== null) {
      items.push({ label: '(No folder)', value: '' });
    }

    // Add all named folders except current
    const sortedFolders = Object.keys(folders)
      .filter(name => name !== '' && name !== currentFolder)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    for (const folderName of sortedFolders) {
      items.push({ label: folderName, value: folderName });
    }

    return items;
  };

  // Folder limit
  const namedFolderCount = Object.keys(folders).filter(k => k !== '').length;
  const folderLimitReached = namedFolderCount >= MAX_FOLDERS;

  const renderFeedContextMenu = (feedId: string) => {
    if (contextMenu?.type !== 'feed' || contextMenu.id !== feedId) return null;
    const submenuItems = getFolderSubmenuItems(feedId);
    return (
      <div ref={contextMenuDropdownRef} className="sidebarMenuDropdown flowFormModal visibleFocus" onClick={(e) => e.stopPropagation()} onKeyDown={menuKeyDown(closeContextMenu)} onBlur={menuBlur(closeContextMenu)}>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('mark-read')}>
          <span className="sidebarMenuLabel">Mark as read</span>
        </button>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} disabled={folderLimitReached} onClick={() => handleContextMenuItemClick('create-folder')}>
          <span className="sidebarMenuLabel">Create folder</span>
        </button>
        {submenuItems.length > 0 && (
          <>
            <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => setOpenSubmenu(openSubmenu === 'move' ? null : 'move')}>
              <span className="sidebarMenuLabel">Move to folder</span>
              <span className={`sidebarMenuChevron ${openSubmenu === 'move' ? 'expanded' : ''}`}><ChevronRightIcon size={14} /></span>
            </button>
            {openSubmenu === 'move' && submenuItems.map(item => (
              <button key={item.value} className="sidebarMenuItem sidebarSubItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('move', item.value)}>
                <span className="sidebarMenuLabel">{item.label}</span>
              </button>
            ))}
          </>
        )}
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('share')}>
          <span className="sidebarMenuLabel">Share feed</span>
        </button>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('rename')}>
          <span className="sidebarMenuLabel">Rename feed</span>
        </button>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('info')}>
          <span className="sidebarMenuLabel">Feed details</span>
        </button>
        {isAdmin && (
          <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('admin')}>
            <span className="sidebarMenuLabel">Admin</span>
          </button>
        )}
        <div className="sidebarMenuDivider"></div>
        <button className="sidebarMenuItem danger flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('unfollow')}>
          <span className="sidebarMenuLabel">{feedById.get(feedId)?.feedStatus == 'NEWSLETTER' ? 'Delete' : 'Unfollow'}</span>
        </button>
      </div>
    );
  };

  const renderFolderContextMenu = (folderName: string) => {
    if (contextMenu?.type !== 'folder' || contextMenu.id !== folderName) return null;
    return (
      <div ref={contextMenuDropdownRef} className="sidebarMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(closeContextMenu)} onBlur={menuBlur(closeContextMenu)}>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('mark-read')}>
          <span className="sidebarMenuLabel">Mark all as read</span>
        </button>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('rename')}>
          <span className="sidebarMenuLabel">Rename folder</span>
        </button>
        <div className="sidebarMenuDivider"></div>
        <button className="sidebarMenuItem danger flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('delete')}>
          <span className="sidebarMenuLabel">Delete folder</span>
        </button>
      </div>
    );
  };

  const renderSpecialContextMenu = (id: 'starred' | 'read-later' | 'all-items') => {
    if (contextMenu?.type !== 'special' || contextMenu.id !== id) return null;
    if (id == 'all-items')
      return (
        <div ref={contextMenuDropdownRef} className="sidebarMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(closeContextMenu)} onBlur={menuBlur(closeContextMenu)}>
          <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('mark-read')}>
            <span className="sidebarMenuLabel">Mark all read</span>
          </button>
        </div>
      );
    return (
      <div ref={contextMenuDropdownRef} className="sidebarMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(closeContextMenu)} onBlur={menuBlur(closeContextMenu)}>
        <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleContextMenuItemClick('remove-all')}>
          <span className="sidebarMenuLabel">Remove all</span>
        </button>
      </div>
    );
  };


  // Subscribe to feed engine changes
  useEffect(() => {
    const unregFeeds = appState.feedEngine.onFeedsChange((newFeeds, newFolders) => {
      setFeeds([...newFeeds]);
      setFolders({ ...newFolders });
      // onUnreadChange only fires on change, so re-seed counts when the feed list arrives/changes
      const counts: Record<string, number> = {};
      for (const feed of newFeeds)
        counts[feed.id] = appState.feedEngine.getUnreadCount(feed.id);
      setUnreadCounts(prev => ({ ...prev, ...counts }));
    });
    const unregUnread = appState.feedEngine.onUnreadChange((feedId, count) => {
      setUnreadCounts(prev => ({ ...prev, [feedId]: count }));
    });
    // Initialize unread counts
    const initial: Record<string, number> = {};
    for (const feed of appState.feedEngine.getFeeds()) {
      initial[feed.id] = appState.feedEngine.getUnreadCount(feed.id);
    }
    setUnreadCounts(initial);
    const unregStar = appState.feedEngine.onStarChange((starred, readLater) => {
      setStarredCount(starred);
      setReadLaterCount(readLater);
    });
    const unregNotice = appState.feedEngine.onNoticeChange(setNotice);
    return () => { unregFeeds(); unregUnread(); unregStar(); unregNotice(); };
  }, [appState.feedEngine]);

  // Update notice time label every 10s
  useEffect(() => {
    if (!notice?.shutdownAtMs) {
      setNoticeTimeLabel('');
      return;
    }
    const update = () => {
      const secsLeft = Math.max(0, Math.round((notice.shutdownAtMs! - Date.now()) / 1000));
      if (secsLeft < 60)
        setNoticeTimeLabel('in a few seconds');
      else
        setNoticeTimeLabel('at ' + new Date(notice.shutdownAtMs!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    };
    update();
    const timer = setInterval(update, 10_000);
    return () => clearInterval(timer);
  }, [notice]);

  // Save expansion state to localStorage whenever it changes
  useEffect(() => {
    try {
      const state = {
        sections: {
          home: isHomeExpanded,
          admin: isAdminExpanded,
          feeds: isFeedsExpanded,
        },
        folders: expandedFolders,
        folderOrder: folderOpenOrder,
      };
      localStorage.setItem('sidebar-expansion', JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save sidebar expansion state:', e);
    }
  }, [isHomeExpanded, isAdminExpanded, isFeedsExpanded, expandedFolders, folderOpenOrder]);

  // Decided once per mount: this is a real "sidebar was opened" (page load, or the mobile drawer
  // coming back — it unmounts while collapsed), not just a page navigation on desktop.
  const [isSidebarShow] = useState(() => {
    const isShow = !didTrimFoldersThisPageLoad || !!isMobile;
    didTrimFoldersThisPageLoad = true;
    return isShow;
  });
  const didTrimFoldersRef = useRef(false);

  // Collapse all but the last MAX_FOLDERS_OPEN_ON_SHOW opened folders. Runs as a layout effect so
  // the over-full tree is never painted, and only once the folder list has arrived (it loads async
  // on a cold start).
  useLayoutEffect(() => {
    if (!isSidebarShow || didTrimFoldersRef.current) return;
    const folderNames = Object.keys(folders).filter(name => name);
    if (!folderNames.length) return;
    didTrimFoldersRef.current = true;
    const openFolders = folderNames.filter(name => isFolderOpen(expandedFolders, name));
    if (openFolders.length <= MAX_FOLDERS_OPEN_ON_SHOW) return;
    const selectedFolder = selectedFeed ? folderNames.find(name => folders[name].includes(selectedFeed)) : undefined;
    const keep = pickFoldersToKeepOpen(openFolders, folderOpenOrder, selectedFolder);
    setExpandedFolders(prev => {
      const next = { ...prev };
      for (const name of openFolders)
        if (!keep.has(name)) next[name] = false;
      return next;
    });
    setFolderOpenOrder(prev => prev.filter(name => keep.has(name)));
  }, [isSidebarShow, folders, expandedFolders, folderOpenOrder, selectedFeed]);

  const getUnread = (feedId: string) => unreadCounts[feedId] ?? appState.feedEngine.getUnreadCount(feedId);

  const feedById = new Map(feeds.map(f => [f.id, f]));
  const namedFolders = Object.fromEntries(Object.entries(folders).filter(([k]) => k !== ''));
  const feedsInNamedFolders = new Set(Object.values(namedFolders).flat());
  const unfiledOrder = folders[''] ?? [];
  const orderedUnfiled = unfiledOrder.map(id => feedById.get(id)).filter((f): f is RestUserFeed => !!f);
  const remainingUnfiled = feeds.filter(f => !feedsInNamedFolders.has(f.id) && !unfiledOrder.includes(f.id));
  const allUnfiledFeeds = [...orderedUnfiled, ...remainingUnfiled];
  // In unread mode we still keep selected / freshly-subscribed feeds visible.
  const isUnreadVisible = (f: RestUserFeed) => getUnread(f.id) || selectedFeed == f.id || appState.feedEngine.isRecentlySubscribed(f.id);

  // Name filter (client-side). When active it searches *all* feeds regardless of
  // unread/all mode; name overrides are already baked into shortTitle by getFeeds().
  const filterQuery = filterActive ? filterText.trim().toLowerCase() : '';
  const isFiltering = !!filterQuery;
  const matchesFilter = (f: RestUserFeed) => (f.shortTitle || f.title).toLowerCase().includes(filterQuery);
  // Decides which feeds appear in a group: filter takes precedence over mode.
  const visibleFeeds = (list: RestUserFeed[]) => isFiltering ? list.filter(matchesFilter) : showAllFeeds ? list : list.filter(isUnreadVisible);

  const unfiledFeeds = visibleFeeds(allUnfiledFeeds);
  const totalUnread = feeds.reduce((sum, f) => sum + getUnread(f.id), 0);
  // The "Unread posts" badge follows the folder badges: tracked ('u') feeds only, '+' when
  // untracked ('a') feeds add unread posts, a dot when only untracked feeds have any.
  const trackedUnread = feeds.reduce((sum, f) => sum + (feedShowsCounts(f.id) ? getUnread(f.id) : 0), 0);
  const hasUntrackedUnread = feeds.some(f => !feedShowsCounts(f.id) && getUnread(f.id));
  const hasVisibleFeeds = showAllFeeds || feeds.some(isUnreadVisible);
  // Show the filter affordance once the current mode would list more than 10 feeds.
  const modeFeedCount = showAllFeeds ? feeds.length : feeds.filter(isUnreadVisible).length;
  const showFilterButton = modeFeedCount > 10;
  const filterMatchCount = isFiltering ? feeds.filter(matchesFilter).length : 0;
  const draggedFeed = draggedFeedId ? feedById.get(draggedFeedId) : undefined;
  const prevCountsRef = useRef({ totalUnread, starredCount, readLaterCount, initialized: false });

  useEffect(() => {
    if (!prevCountsRef.current.initialized) {
      prevCountsRef.current = { totalUnread, starredCount, readLaterCount, initialized: true };
      return;
    }
    if (totalUnread > prevCountsRef.current.totalUnread) {
      setNewPostsIconAnimTick(v => v + 1);
    }
    if (starredCount > prevCountsRef.current.starredCount) {
      setStarIconAnimTick(v => v + 1);
    }
    if (readLaterCount > prevCountsRef.current.readLaterCount) {
      setReadLaterIconAnimTick(v => v + 1);
    }
    prevCountsRef.current = { totalUnread, starredCount, readLaterCount, initialized: true };
  }, [totalUnread, starredCount, readLaterCount]);

  // Selecting a feed that isn't in the filtered list (e.g. "Next Feed" in PostView)
  // exits filter mode. Only watch selectedFeed so typing a query that excludes the
  // current selection doesn't clear it mid-keystroke.
  useEffect(() => {
    if (!filterActive || !selectedFeed) return;
    const query = filterText.trim().toLowerCase();
    if (!query) return;
    const feed = feedById.get(selectedFeed);
    if (!feed || !(feed.shortTitle || feed.title).toLowerCase().includes(query)) clearFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed]);

  const renderDropGhost = () => {
    if (!draggedFeed) return null;
    const unread = getUnread(draggedFeed.id);
    return (
      <div className="feedItem dropGhost">
        <div className="feedItemContent">
          <FeedIcon className="feedIcon" src={getFeedIconUrl(draggedFeed, 32)} size={16} circular={draggedFeed.circularIcon} fallback={<span className="feedIcon">📰</span>} />
          <span className="feedName">{draggedFeed.shortTitle || draggedFeed.title}</span>
        </div>
        <UnreadIndicator count={unread} showCounts={feedShowsCounts(draggedFeed.id)} paused={appState.feedEngine.isFeedPaused(draggedFeed.id)} />
      </div>
    );
  };

  const toggleFolder = (name: string) => {
    const willOpen = !isFolderOpen(expandedFolders, name);
    setExpandedFolders(prev => ({ ...prev, [name]: !isFolderOpen(prev, name) }));
    setFolderOpenOrder(prev => updateFolderOpenOrder(prev, name, willOpen));
  };

  // A feed selected from outside the sidebar (Next Feed / Skip Feed, keyboard) may sit in a
  // collapsed folder; open it so the selection is visible. Only for the permanent desktop
  // column — the overlay drawer closes on selection anyway, so its folders are left alone.
  // Each selection is handled once (the ref), so a later folder-data refresh doesn't re-open
  // a folder the user collapsed meanwhile; folders may still be loading on a cold start,
  // hence the effect also watches `folders`.
  const autoOpenedForFeedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isDrawer || !selectedFeed || autoOpenedForFeedRef.current == selectedFeed) return;
    const folderNames = Object.keys(folders).filter(name => name);
    if (!folderNames.length) return;
    autoOpenedForFeedRef.current = selectedFeed;
    const folderName = folderNames.find(name => folders[name].includes(selectedFeed));
    if (!folderName || isFolderOpen(expandedFolders, folderName)) return;
    setExpandedFolders(prev => ({ ...prev, [folderName]: true }));
    setFolderOpenOrder(prev => updateFolderOpenOrder(prev, folderName, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, folders, isDrawer]);

  // Scroll selected feed into view if it's outside the viewport. Also re-checked when folders
  // expand, because the selected row may only appear once its (auto-)opened folder renders —
  // the ref makes sure each selection is scrolled to at most once.
  const scrolledToFeedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el || !selectedFeed || scrolledToFeedRef.current == selectedFeed) return;
    const item = el.querySelector('.feedItem.selected, .flatItem.selected') as HTMLElement | null;
    if (!item) return;
    scrolledToFeedRef.current = selectedFeed;
    const elRect = el.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top < elRect.top || itemRect.bottom > elRect.bottom)
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedFeed, expandedFolders]);



  const toggleSpecialSection = () => {
    setIsHomeExpanded(!isHomeExpanded);
  };

  const toggleAdminSection = () => {
    setIsAdminExpanded(!isAdminExpanded);
  };

  const toggleFeedsSection = () => {
    setIsFeedsExpanded(!isFeedsExpanded);
  };

  // Drag & drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, feedId: string) => {
    setDraggedFeedId(feedId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', feedId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFeedId(null);
    setDropIndicator(null);
  }, []);

  // dragover fires continuously while dragging; returning the previous object when the
  // target hasn't moved lets React bail out instead of re-rendering the whole sidebar.
  const updateDropIndicator = useCallback((folder: string | null, beforeFeedId: string | null) => {
    setDropIndicator(prev => prev && prev.folder == folder && prev.beforeFeedId == beforeFeedId ? prev : { folder, beforeFeedId });
  }, []);

  const handleContainerDragOver = (e: React.DragEvent, folder: string | null, feedIds: string[]) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    // Find all feedItem children, excluding the ghost and hidden dragged element;
    // their order matches feedIds without the dragged feed.
    const container = e.currentTarget as HTMLElement;
    const allElements = Array.from(container.querySelectorAll(':scope > div > .feedItem, :scope > .feedItem'));
    const feedElements = allElements.filter(el => !el.classList.contains('dropGhost') && !el.classList.contains('dragging'));
    const visibleIds = feedIds.filter(id => id != draggedFeedId);

    // Anchor on the first row whose midpoint is below the cursor; none = end of group
    const y = e.clientY;
    let beforeFeedId: string | null = null;
    for (let i = 0; i < feedElements.length && i < visibleIds.length; i++) {
      const rect = feedElements[i].getBoundingClientRect();
      if (y < rect.top + rect.height / 2) {
        beforeFeedId = visibleIds[i];
        break;
      }
    }
    updateDropIndicator(folder, beforeFeedId);
  };

  const handleFolderHeaderDragOver = (e: React.DragEvent, folderName: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    updateDropIndicator(folderName, null);
  };


  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    // onDrop is attached at two nesting levels; without this, one drop runs moveFeed twice
    e.stopPropagation();
    if (!dropIndicator || !draggedFeedId) return;
    const feedId = draggedFeedId;
    const { folder, beforeFeedId } = dropIndicator;
    setDraggedFeedId(null);
    setDropIndicator(null);

    // Resolve the anchor against the full stored order — visible indices don't apply,
    // since unread mode and filtering hide rows. Unfiled feeds that were never
    // explicitly ordered render after the stored ones; moveFeed materializes the same
    // order. The engine inserts after removing the dragged feed, so index the list
    // without it.
    const stored = folders[folder ?? ''] ?? [];
    const fullList = folder != null ? stored
      : [...stored, ...allUnfiledFeeds.map(f => f.id).filter(id => !stored.includes(id))];
    const withoutDragged = fullList.filter(id => id != feedId);
    const anchorIdx = beforeFeedId != null ? withoutDragged.indexOf(beforeFeedId) : -1;
    await appState.feedEngine.moveFeed(feedId, folder, anchorIdx >= 0 ? anchorIdx : withoutDragged.length);
  };

  const showMarkAllReadConfirmation = useConfirmationModal({
    title: 'Mark all read',
    confirmContent: 'Mark all read',
    dismissContent: 'Cancel',
    children: `Mark all posts in all ${feeds.length} feeds as read?`,
    onConfirm: () => {
      for (const feed of feeds)
        appState.feedEngine.markAllRead(feed.id);
      if (selectedFeed == 'all-items')
        onSelectFeed?.('all-items');
    },
  });

  const showDuplicateWarning = useConfirmationModal({
    title: 'Folder already exists',
    confirmContent: 'OK',
    allowDismiss: false,
    children: 'A folder with this name already exists. Please choose a different name.',
  });

  const handleAddFolderClick = () => {
    setIsCreatingFolder(true);
    setNewFolderName('');
  };

  useEffect(() => {
    if (isCreatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [isCreatingFolder]);

  // Auto-focus first menu item when feeds section menu opens via keyboard
  useEffect(() => {
    if (!showFeedsSectionMenu) return;
    const dropdown = feedsSectionMenuRef.current?.querySelector('.sidebarMenuDropdown');
    if (!dropdown) return;
    // The 'visibleFocus' (keyboard mode) class is set by setFocusMode on the
    // '.flowForm' element, which is the sidebar root — not sidebarRef (.sidebarBody).
    if (!sidebarRef.current?.closest('.flowForm')?.classList.contains('visibleFocus')) return;
    const first = dropdown.querySelector('.flowFocussable:not(:disabled)') as HTMLElement | null;
    if (first) requestAnimationFrame(() => first.focus());
  }, [showFeedsSectionMenu]);

  // Close feeds section menu on outside click
  useEffect(() => {
    if (!showFeedsSectionMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (feedsSectionMenuRef.current && !feedsSectionMenuRef.current.contains(e.target as Node))
        setShowFeedsSectionMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFeedsSectionMenu]);

  // Auto-focus first menu item when feed/folder context menu opens via keyboard
  useEffect(() => {
    if (!contextMenu) return;
    // The 'visibleFocus' (keyboard mode) class is set by setFocusMode on the
    // '.flowForm' element, which is the sidebar root — not sidebarRef (.sidebarBody).
    if (!sidebarRef.current?.closest('.flowForm')?.classList.contains('visibleFocus')) return;
    const dropdown = sidebarRef.current?.querySelector('.feedMenuContainer > .sidebarMenuDropdown, .folderMenuContainer > .sidebarMenuDropdown') as HTMLElement | null;
    if (!dropdown) return;
    const first = dropdown.querySelector('.flowFocussable:not(:disabled)') as HTMLElement | null;
    if (first) requestAnimationFrame(() => first.focus());
  }, [contextMenu]);

  // Close feed/folder context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      const dropdown = sidebarRef.current?.querySelector('.feedMenuContainer > .sidebarMenuDropdown, .folderMenuContainer > .sidebarMenuDropdown');
      if (!dropdown) { closeContextMenu(); return; }
      const container = dropdown.parentElement!;
      if (!container.contains(e.target as Node)) closeContextMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  // Position context menus to stay within viewport
  useMenuPosition(!!contextMenu, contextMenuTriggerRef, contextMenuDropdownRef);
  useMenuPosition(showFeedsSectionMenu, feedsSectionMenuTriggerRef, feedsSectionMenuDropdownRef);

  const handleFolderNameBlur = async () => {
    const trimmedName = newFolderName.trim();

    if (!trimmedName) {
      setIsCreatingFolder(false);
      setNewFolderName('');
      return;
    }

    const folders = appState.feedEngine.getFolders();
    if (folders[trimmedName]) {
      showDuplicateWarning();
      setIsCreatingFolder(false);
      setNewFolderName('');
      return;
    }

    const success = await appState.feedEngine.addEmptyFolder(trimmedName);
    if (success) {
      setIsCreatingFolder(false);
      setNewFolderName('');
    }
  };

  const handleFolderNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      e.currentTarget.blur();
    } else if (e.key == 'Escape') {
      setIsCreatingFolder(false);
      setNewFolderName('');
    }
  };

  // Focus rename input when it appears; when it closes via keyboard, return focus
  // to the folder header (the new name if it was renamed). rAF waits for the header
  // to re-render and, on rename, the new folder name to land in the DOM.
  useEffect(() => {
    if (renamingFolder && renameFolderInputRef.current) {
      renameFolderInputRef.current.focus();
      renameFolderInputRef.current.select();
    } else if (!renamingFolder && refocusFolderRef.current != null) {
      const name = refocusFolderRef.current;
      refocusFolderRef.current = null;
      requestAnimationFrame(() => {
        const header = sidebarRef.current?.querySelector(`.folderHeader[data-folder-name="${CSS.escape(name)}"]`) as HTMLElement | null;
        header?.focus();
      });
    }
  }, [renamingFolder]);

  const handleRenameFolderBlur = async () => {
    if (!renamingFolder) return;

    const trimmedName = renameFolderValue.trim();
    const oldName = renamingFolder;
    // Keyboard (Enter) commits should return focus to the folder header; remember
    // which name to land on, defaulting to oldName and switching to the new name
    // only once the rename actually succeeds. Reset the one-shot keyboard flag.
    const viaKeyboard = renameViaKeyboardRef.current;
    renameViaKeyboardRef.current = false;
    if (viaKeyboard) refocusFolderRef.current = oldName;

    // If empty or same name, just cancel
    if (!trimmedName || trimmedName === oldName) {
      setRenamingFolder(null);
      setRenameFolderValue('');
      return;
    }

    // Check for duplicate
    const currentFolders = appState.feedEngine.getFolders();
    if (currentFolders[trimmedName]) {
      showDuplicateWarning();
      setRenamingFolder(null);
      setRenameFolderValue('');
      return;
    }

    const success = await appState.feedEngine.renameFolder(oldName, trimmedName);
    if (success) {
      if (viaKeyboard) refocusFolderRef.current = trimmedName;
      // Update expanded state for renamed folder
      setExpandedFolders(prev => {
        const newState = { ...prev };
        if (oldName in newState) {
          newState[trimmedName] = newState[oldName];
          delete newState[oldName];
        }
        return newState;
      });
      setFolderOpenOrder(prev => prev.map(n => n == oldName ? trimmedName : n));
    }
    setRenamingFolder(null);
    setRenameFolderValue('');
  };

  const handleRenameFolderKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      renameViaKeyboardRef.current = true;
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      refocusFolderRef.current = renamingFolder;
      setRenamingFolder(null);
      setRenameFolderValue('');
    }
  };

  return (
    <div className="leftSidebar flowForm">
      <div className="sidebarHeader">
        <div className="sidebarHeaderContent">
          <a
            href="https://downread.com"
            className="sidebarLogoLink"
            onClick={(e) => {
              if (onCollapse && window.matchMedia('(max-width: 750px)').matches) {
                e.preventDefault();
                onCollapse();
              }
            }}
          >
            <DownreadLogo size={32} showText/>
            <span className="betaBadge" aria-label="Beta">beta</span>
          </a>
        </div>
      </div>

      <div className="sidebarBody" ref={sidebarRef}>
      {notice && (
        <div className={`sidebarNotice ${notice.shutdownAtMs ? 'shutdown' : ''}`}>
          <span className="sidebarNoticeMessage">{notice.message}</span>
          {noticeTimeLabel && <span className="sidebarNoticeTime">{noticeTimeLabel}</span>}
        </div>
      )}

      <div className="sidebarSection">
          <div className="feedList">
            <A
              href="/a/home"
              className={`flatItem flowFocussable ${selectedFeed === 'home' ? 'selected' : ''}`}
              internal={true}
              onClick={() => onMenuClick?.()}
            >
              <span className="itemIcon"><HomeIcon size={16} /></span>
              <span className="folderName">Home</span>
            </A>
            <div
              className={`flatItem flowFocussable ${selectedFeed === 'all-items' && !activeNewFolder ? 'selected' : ''}`}
              tabIndex={0}
              onClick={() => handleFeedClick('all-items')}
              onContextMenu={(e) => { e.preventDefault(); handleContextMenuClick(e, 'special', 'all-items'); }}
            >
              <span className="itemIcon">
                <NewPostsIcon key={`new-posts-${newPostsIconAnimTick}`} size={16} className={newPostsIconAnimTick ? 'newPostsIconPageAdd' : undefined} />
              </span>
              <span className="folderName">Unread posts</span>
              <div className="feedMenuContainer">
                <button
                  className="contextMenuButton flowFocussable sidewayFocus"
                  onMouseDown={handleContextMenuMouseDown}
                  onClick={(e) => handleContextMenuClick(e, 'special', 'all-items')}
                  title="Unread posts options"
                >
                  <MoreDotsIcon size={16} />
                </button>
                {renderSpecialContextMenu('all-items')}
              </div>
              {feedsLoading
                ? <Spinner spinning className="unreadCount" />
                : <UnreadIndicator count={trackedUnread || (hasUntrackedUnread ? 1 : 0)} showCounts={trackedUnread > 0} plus={hasUntrackedUnread} />}
            </div>
            <div
              className={`flatItem flowFocussable ${selectedFeed === 'read-later' ? 'selected' : ''}`}
              tabIndex={0}
              onClick={() => handleFeedClick('read-later')}
              onContextMenu={(e) => { e.preventDefault(); handleContextMenuClick(e, 'special', 'read-later'); }}
            >
              <span className="itemIcon">
                <ClockIcon key={`clock-${readLaterIconAnimTick}`} size={16} className={readLaterIconAnimTick ? 'clockIconTick' : undefined} />
              </span>
              <span className="folderName">Read later</span>
              <div className="feedMenuContainer">
                <button
                  className="contextMenuButton flowFocussable sidewayFocus"
                  onMouseDown={handleContextMenuMouseDown}
                  onClick={(e) => handleContextMenuClick(e, 'special', 'read-later')}
                  title="Read later options"
                >
                  <MoreDotsIcon size={16} />
                </button>
                {renderSpecialContextMenu('read-later')}
              </div>
              {readLaterCount > 0 && <span className="unreadCount">{readLaterCount}</span>}
            </div>
            <div
              className={`flatItem flowFocussable ${selectedFeed === 'starred' ? 'selected' : ''}`}
              tabIndex={0}
              onClick={() => handleFeedClick('starred')}
              onContextMenu={(e) => { e.preventDefault(); handleContextMenuClick(e, 'special', 'starred'); }}
            >
              <span className="itemIcon">
                <StarIcon key={`star-${starIconAnimTick}`} size={16} className={starIconAnimTick ? 'starIconCelebrate' : undefined} />
              </span>
              <span className="folderName">Starred</span>
              <div className="feedMenuContainer">
                <button
                  className="contextMenuButton flowFocussable sidewayFocus"
                  onMouseDown={handleContextMenuMouseDown}
                  onClick={(e) => handleContextMenuClick(e, 'special', 'starred')}
                  title="Starred options"
                >
                  <MoreDotsIcon size={16} />
                </button>
                {renderSpecialContextMenu('starred')}
              </div>
              {starredCount > 0 && <span className="unreadCount">{starredCount}</span>}
            </div>
            <A
              href="/a/myfeeds"
              className={`flatItem flowFocussable ${selectedFeed === 'myfeeds' ? 'selected' : ''}`}
              internal={true}
              onClick={() => onMenuClick?.()}
            >
              <span className="itemIcon"><ManageFeedsIcon size={16} /></span>
              <span className="folderName">My feeds</span>
            </A>
          </div>
      </div>

      {isAdmin && (
        <div className="sidebarSection collapsible">
          <div className="sectionHeader flowFocussable" tabIndex={0} onClick={toggleAdminSection}>
            <span className="sectionHeaderTitle">
              {/* Pending content reports are legally time-sensitive, so the whole Admin
                  section header turns red when any are open (see LEGAL.md). */}
              <h3 style={reportsCount ? { color: 'var(--error-color)' } : undefined}>Admin</h3>
              {/* When the admin secret hasn't been entered we can't poll notifications, so show a
                  warning in place of the count instead of a misleading empty/zero badge. */}
              {needsAdminSecret
                ? (
                  <span
                    className="adminTodoBadge"
                    style={{ background: 'var(--warning-color, #d98c00)' }}
                    title="Enter the admin secret to load notifications"
                  >
                    !
                  </span>
                )
                : (() => {
                  /* Sum of all badged numbers shown within the admin section (Feed TODOs +
                     recently-inactive feeds + content reports + Yellowfeeds queue). Add new admin badge counts to this total. */
                  const adminBadgeTotal = todosCount + inactiveFeedsCount + reportsCount + yellowfeedsCount;
                  if (!adminBadgeTotal) return null;
                  return (
                    <span
                      className="adminTodoBadge"
                      style={reportsCount ? { background: 'var(--error-color)' } : undefined}
                      title={`${todosCount} feed TODO${todosCount == 1 ? '' : 's'} pending, ${inactiveFeedsCount} recently inactive feed${inactiveFeedsCount == 1 ? '' : 's'}, ${reportsCount} content report${reportsCount == 1 ? '' : 's'}, ${yellowfeedsCount} Yellowfeeds review${yellowfeedsCount == 1 ? '' : 's'}`}
                    >
                      {adminBadgeTotal >= 1000 ? '>999' : adminBadgeTotal}
                    </span>
                  );
                })()}
            </span>
            <span className={`expandIcon ${isAdminExpanded ? 'expanded' : ''}`}>
              <ChevronRightIcon size={16} />
            </span>
          </div>
          {isAdminExpanded && (
            <div className="feedList">
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-feeds' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleAdminClick}
              >
                <span className="itemIcon">🔧</span>
                <span className="folderName">Feed admin</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'feed-todos' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleFeedTodosClick}
              >
                <span className="itemIcon">✅</span>
                <span className="folderName">Feed TODOs</span>
                {todosCount > 0 && (
                  <span className="unreadCount">
                    {todosCount >= 1000 ? '>999' : todosCount}
                  </span>
                )}
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-reports' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleReportsAdminClick}
                style={reportsCount ? { color: 'var(--error-color)', fontWeight: 700 } : undefined}
              >
                <span className="itemIcon"><FlagIcon size={16} color={reportsCount ? 'var(--error-color)' : 'currentColor'} /></span>
                <span className="folderName">Reports</span>
                {reportsCount > 0 && (
                  <span className="unreadCount" style={{ background: 'transparent', color: 'var(--error-color)', fontWeight: 700 }}>
                    {reportsCount >= 100 ? '>99' : reportsCount}
                  </span>
                )}
              </div>
              <div
                className="flatItem flowFocussable"
                tabIndex={0}
                onClick={handleYellowfeedsAdminClick}
                title="Claims and listing changes to review on Yellowfeeds (opens a new tab)"
              >
                <span className="itemIcon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 4a15 15 0 0 1 15 15"/>
                    <path d="M5 10a9 9 0 0 1 9 9"/>
                    <circle cx="6" cy="18" r="1.5" fill="currentColor"/>
                  </svg>
                </span>
                <span className="folderName">Yellowfeeds</span>
                {yellowfeedsCount > 0 && (
                  <span className="unreadCount">
                    {yellowfeedsCount >= 100 ? '>99' : yellowfeedsCount}
                  </span>
                )}
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-retention' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleRetentionAdminClick}
              >
                <span className="itemIcon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2.5l8 3v5.5c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V5.5z"/>
                  </svg>
                </span>
                <span className="folderName">Retention</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-debug' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleDebugFeedClick}
              >
                <span className="itemIcon">🐛</span>
                <span className="folderName">Debug RSS</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-discover' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleDiscoverAdminClick}
              >
                <span className="itemIcon">✨</span>
                <span className="folderName">Discover</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-inactive-feeds' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleInactiveFeedsAdminClick}
              >
                <span className="itemIcon">💤</span>
                <span className="folderName">Inactive feeds</span>
                {inactiveFeedsCount > 0 && (
                  <span className="unreadCount">
                    {inactiveFeedsCount >= 100 ? '>99' : inactiveFeedsCount}
                  </span>
                )}
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-images' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleImagesAdminClick}
              >
                <span className="itemIcon">🖼️</span>
                <span className="folderName">Images</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-catalogs' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleCatalogsAdminClick}
              >
                <span className="itemIcon">📚</span>
                <span className="folderName">Feed catalog</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-users' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleUsersAdminClick}
              >
                <span className="itemIcon">👥</span>
                <span className="folderName">Users</span>
              </div>
              <div
                className={`flatItem flowFocussable ${selectedFeed === 'admin-invitations' ? 'selected' : ''}`}
                tabIndex={0}
                onClick={handleInvitationsAdminClick}
              >
                <span className="itemIcon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-10 6L2 7"/>
                  </svg>
                </span>
                <span className="folderName">Invitations</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sidebarSection collapsible">
        {filterActive ? (
          <div className="sectionHeader feedFilterHeader">
            <span className="feedFilterIcon"><FilterIcon size={16} /></span>
            <input
              ref={filterInputRef}
              type="text"
              className="feedFilterInput flowFocussable"
              value={filterText}
              placeholder="Filter feeds…"
              onChange={(e) => setFilterText(e.target.value)}
              onBlur={handleFilterBlur}
              onKeyDown={handleFilterKeyDown}
            />
            <button
              className="feedFilterClear flowFocussable sidewayFocus"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearFilter}
              title="Clear filter"
              aria-label="Clear filter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>
        ) : (
        <div className="sectionHeader clickable feedsHeader flowFocussable" tabIndex={0} onClick={handleFeedsHeaderClick} onContextMenu={(e) => { e.preventDefault(); handleFeedsSectionMenuClick(e); }}>
          <h3 ref={feedsHeaderRef} className={showAllWhereFlashClass}>{showAllFeeds ? 'All feeds' : 'Unread feeds'}</h3>
          <button
            className={`addFeedButton flowFocussable sidewayFocus${selectedFeed == 'add' ? ' selected' : ''}`}
            onMouseDown={handleContextMenuMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              handleAddFeedClick();
            }}
            title="Add feed"
          >
            <PlusIcon size={16} />
          </button>
          <div className="feedsSectionMenuContainer" ref={feedsSectionMenuRef}>
            {showFilterButton && (
              <button
                ref={filterButtonRef}
                className="contextMenuButton flowFocussable sidewayFocus"
                onMouseDown={handleContextMenuMouseDown}
                onClick={handleFilterButtonClick}
                title="Filter feeds"
              >
                <FilterIcon size={16} />
              </button>
            )}
            <button
              ref={feedsSectionMenuTriggerRef}
              className="contextMenuButton flowFocussable sidewayFocus"
              onMouseDown={handleContextMenuMouseDown}
              onClick={handleFeedsSectionMenuClick}
              title="Feeds options"
            >
              <MoreDotsIcon size={16} />
            </button>
            {showFeedsSectionMenu && (
              <div ref={feedsSectionMenuDropdownRef} className="sidebarMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowFeedsSectionMenu(false))} onBlur={menuBlur(() => setShowFeedsSectionMenu(false))}>
                <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleFeedsSectionMenuItemClick('add-feed')}>
                  <span className="sidebarMenuLabel">Add feed</span>
                </button>
                <button className="sidebarMenuItem flowFocussable" tabIndex={0} disabled={folderLimitReached} onClick={() => handleFeedsSectionMenuItemClick('create-folder-section')}>
                  <span className="sidebarMenuLabel">Create folder</span>
                </button>
                <button className="sidebarMenuItem flowFocussable" tabIndex={0} onClick={() => handleFeedsSectionMenuItemClick('toggle-show-all-feeds')}>
                  <span className="sidebarMenuLabel">Show all feeds</span>
                  {showAllFeeds && <span className="sidebarMenuCheck">✓</span>}
                </button>
              </div>
            )}
          </div>
        </div>
        )}
        <div className="feedList">
          {feedsLoading && !feeds.length && (
            <div className="feedsLoadingSpinner"><Spinner delayed /></div>
          )}
          {feedEngineState == 'ERROR' && !feeds.length && (
            // Failed load: an empty feed list here reads as "subscriptions gone" — show why instead.
            <FeedLoadError compact />
          )}
          {isCreatingFolder && (
            <div className="folderItem">
              <div className="folderHeader">
                <span className="folderExpandIcon">
                  <ChevronRightIcon size={14} />
                </span>
                <input
                  ref={newFolderInputRef}
                  type="text"
                  className="folderNameInput"
                  value={newFolderName}
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(/(^\s|[\n\r\t])/g, '').slice(0, 32);
                    setNewFolderName(sanitized);
                  }}
                  onBlur={handleFolderNameBlur}
                  onKeyDown={handleFolderNameKeyDown}
                  placeholder="New folder"
                  maxLength={32}
                />
              </div>
            </div>
          )}
          {Object.entries(namedFolders).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map(([folderName, feedIds]) => {
            const allFolderFeeds = feedIds.map(id => feedById.get(id)).filter((f): f is RestUserFeed => !!f);
            // Folder badge: sum only the unread of count-style ('u') feeds. If that's
            // zero but a dot-style ('a') feed has unread, show a dot instead; otherwise
            // nothing. (UnreadIndicator: count>0 + showCounts -> number; count>0 without
            // showCounts -> dot; count 0 -> empty placeholder.)
            const folderCountUnread = allFolderFeeds.reduce((sum, f) => sum + (feedShowsCounts(f.id) ? getUnread(f.id) : 0), 0);
            const folderHasDot = allFolderFeeds.some(f => !feedShowsCounts(f.id) && getUnread(f.id));
            const folderFeeds = visibleFeeds(allFolderFeeds);
            // A shortcut to /a/new/<folder> ("read all unread in this folder"). The context
            // button on the folder header does the same, but is hard to find, so we surface a
            // dedicated row whenever more than one feed in the folder has unread posts.
            const unreadFeedCount = allFolderFeeds.filter(f => getUnread(f.id)).length;
            // Keep the row while its view is open even if marking-read drops the count,
            // so the selection indicator doesn't vanish under the user.
            const showReadAll = (unreadFeedCount > 1 || activeNewFolder == folderName) && !isFiltering;
            const isRenaming = renamingFolder === folderName;
            // Auto-expand folders while filtering so matches are immediately visible.
            const isExpanded = isFiltering ? true : (expandedFolders[folderName] ?? true);
            // Hide a folder with nothing to show: no matches while filtering, or — in unread
            // mode — a folder whose feeds are all read. Genuinely empty folders stay visible in
            // every mode (only a filter, which they can't match, hides them).
            if (!folderFeeds.length && !isRenaming) {
              if (isFiltering) return null;
              if (!showAllFeeds && allFolderFeeds.length) return null;
            }
            return (
              <div className={`folderItem ${dropIndicator?.folder == folderName && draggedFeedId ? 'dragOver' : ''}`} key={folderName} onDrop={handleDrop}>
                <div
                  className="folderHeader flowFocussable"
                  data-folder-name={folderName}
                  tabIndex={0}
                  onClick={() => !isRenaming && toggleFolder(folderName)}
                  onDragOver={(e) => handleFolderHeaderDragOver(e, folderName)}
                >
                  <span className={`folderExpandIcon ${isExpanded ? 'expanded' : ''}`}>
                    <ChevronRightIcon size={14} />
                  </span>
                  {isRenaming ? (
                    <input
                      ref={renameFolderInputRef}
                      type="text"
                      className="folderNameInput"
                      value={renameFolderValue}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/(^\s|[\n\r\t])/g, '').slice(0, 32);
                        setRenameFolderValue(sanitized);
                      }}
                      onBlur={handleRenameFolderBlur}
                      onKeyDown={handleRenameFolderKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      maxLength={32}
                    />
                  ) : (
                    <>
                      <span className="folderName">{folderName}</span>
                      <div className="folderMenuContainer">
                        <button
                          className="contextMenuButton folderReadAllButton flowFocussable sidewayFocus"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            // With a single unread feed, /a/new/<folder> would just be that
                            // one feed — open it directly instead.
                            const unreadFeeds = allFolderFeeds.filter(f => getUnread(f.id));
                            if (unreadFeeds.length == 1)
                              goTo(routerCtx, `/a/feed/${unreadFeeds[0].id}`);
                            else
                              goTo(routerCtx, `/a/new/${encodeURIComponent(folderName)}`);
                            onMenuClick?.();
                          }}
                          title={`Read unread posts in ${folderName}`}
                        >
                          <NewPostsIcon size={16} />
                        </button>
                        <button
                          className="contextMenuButton flowFocussable sidewayFocus"
                          onMouseDown={handleContextMenuMouseDown}
                          onClick={(e) => handleContextMenuClick(e, 'folder', folderName)}
                          title="Folder options"
                        >
                          <MoreDotsIcon size={16} />
                        </button>
                        {renderFolderContextMenu(folderName)}
                      </div>
                      {/* Kept rendered (just hidden) when expanded so the context
                          button doesn't shift as the count appears/disappears. */}
                      <span className={`folderUnreadSlot ${isExpanded ? 'hidden' : ''}`}>
                        <UnreadIndicator count={folderCountUnread || (folderHasDot ? 1 : 0)} showCounts={folderCountUnread > 0} plus={folderHasDot} />
                      </span>
                    </>
                  )}
                </div>
                {isExpanded && (
                  <div className="folderContents" onDragOver={(e) => handleContainerDragOver(e, folderName, folderFeeds.map(f => f.id))} onDrop={handleDrop}>
                    {showReadAll && (
                      <div
                        className={`feedItem readAllItem flowFocussable ${activeNewFolder == folderName ? 'selected' : ''}`}
                        tabIndex={0}
                        onClick={() => {
                          goTo(routerCtx, `/a/new/${encodeURIComponent(folderName)}`);
                          onMenuClick?.();
                        }}
                        title={`Read unread posts in ${folderName}`}
                      >
                        <div className="feedItemContent">
                          <span className="feedIcon"><NewPostsIcon size={16} /></span>
                          <span className="feedName">Read all</span>
                        </div>
                        <UnreadIndicator count={folderCountUnread || (folderHasDot ? 1 : 0)} showCounts={folderCountUnread > 0} plus={folderHasDot} />
                      </div>
                    )}
                    {!folderFeeds.length && (
                      <div className="folderEmpty">Drag feeds here to organize them</div>
                    )}
                    {folderFeeds.map(feed => (
                      <Fragment key={feed.id}>
                        {dropIndicator?.beforeFeedId == feed.id && renderDropGhost()}
                        <FeedRow
                          feed={feed}
                          unread={getUnread(feed.id)}
                          showCounts={feedShowsCounts(feed.id)}
                          paused={appState.feedEngine.isFeedPaused(feed.id)}
                          loading={appState.feedEngine.isRefreshing(feed.id)}
                          selected={selectedFeed == feed.id}
                          dragging={draggedFeedId == feed.id}
                          menu={renderFeedContextMenu(feed.id)}
                          onFeedClick={handleFeedClick}
                          onContextMenuClick={handleContextMenuClick}
                          onContextMenuMouseDown={handleContextMenuMouseDown}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onNewsletterInfo={setNewsletterInfoId}
                          onPrivateFeedInfo={openPrivateFeedInfo}
                          onIssueInfo={setIssueFeedId}
                        />
                      </Fragment>
                    ))}
                    {dropIndicator?.folder == folderName && !dropIndicator.beforeFeedId && renderDropGhost()}
                  </div>
                )}
              </div>
            );
          })}
          <div onDragOver={(e) => handleContainerDragOver(e, null, unfiledFeeds.map(f => f.id))} onDrop={handleDrop}>
            {unfiledFeeds.map(feed => (
              <Fragment key={feed.id}>
                {dropIndicator?.beforeFeedId == feed.id && renderDropGhost()}
                <FeedRow
                  feed={feed}
                  unread={getUnread(feed.id)}
                  showCounts={feedShowsCounts(feed.id)}
                  paused={appState.feedEngine.isFeedPaused(feed.id)}
                  loading={appState.feedEngine.isRefreshing(feed.id)}
                  selected={selectedFeed == feed.id}
                  dragging={draggedFeedId == feed.id}
                  menu={renderFeedContextMenu(feed.id)}
                  onFeedClick={handleFeedClick}
                  onContextMenuClick={handleContextMenuClick}
                  onContextMenuMouseDown={handleContextMenuMouseDown}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onNewsletterInfo={setNewsletterInfoId}
                  onPrivateFeedInfo={openPrivateFeedInfo}
                  onIssueInfo={setIssueFeedId}
                />
              </Fragment>
            ))}
            {!!dropIndicator && dropIndicator.folder == null && !dropIndicator.beforeFeedId && renderDropGhost()}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                updateDropIndicator(null, null);
              }}
              onDrop={handleDrop}
            >
              {isFiltering && !filterMatchCount && (
                <p className="unreadFeedsEmpty">No feeds match “{filterText.trim()}”.</p>
              )}
              {!isFiltering && !showAllFeeds && !hasVisibleFeeds && !!feeds.length && !feedsLoading && (
                <p className="unreadFeedsEmpty">
                  No unread feeds! Click on the headline (<span ref={showAllWhereRef}><FocusText className="unreadFeedsWhereLink" focusVisible={false} focussable="flow" onClick={triggerShowAllWhere}>where?</FocusText></span>) to see all feeds.
                </p>
              )}
              {!isFiltering && (
                <A
                  href="/a/add"
                  className="flatItem placeholder flowFocussable"
                  onClick={() => onMenuClick?.()}
                  onKeyDown={() => {}}
                  internal={true}
                >
                  + Add feed
                </A>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 300, flexShrink: 0 }} />
      </div>

      <div className="sidebarFooter">
        <button className="sidebarFooterButton flowFocussable" onClick={handlePreferencesClick} title="Settings">
          <SettingsIcon size={18} />
        </button>
        <div className="sidebarFooterInfo">
          {(() => {
            const capabilities = appState.state.authData?.selectedMembership?.capabilities;
            const userPlan = appState.state.currentUser?.userPlan;
            const items: React.ReactNode[] = [];
            if (isOwner(capabilities))
              items.push(<Badge key="role">Owner</Badge>);
            else if (isAdmin)
              items.push(<Badge key="role">Admin</Badge>);
            else if (userPlan == 'vip')
              items.push(<Badge key="plan">VIP</Badge>);
            else if (userPlan == 'pro')
              items.push(<Badge key="plan">Pro</Badge>);
            else if (userPlan == 'earlyAdopter')
              items.push(<Badge key="plan">Early Adopter</Badge>);

            if (process.env.REACT_APP_BUILD_NUMBER)
              items.push(<Badge key="env">b{process.env.REACT_APP_BUILD_NUMBER}</Badge>);

            const atLimit = userPlan && feeds.length >= getPlanLimits(userPlan).maxFeeds;
            items.push(<span key="feeds" className={`sidebarFooterText${atLimit ? ' atLimit' : ''}`}>{feeds.length} {feeds.length == 1 ? 'feed' : 'feeds'}</span>);
            items.push(<span key="unread" className="sidebarFooterText">{totalUnread} unread {totalUnread == 1 ? 'post' : 'posts'}</span>);
            return items;
          })()}
        </div>
        <button className="sidebarFooterButton flowFocussable" onClick={handleAccountClick} title="Account">
          <UserIcon size={18} />
        </button>
      </div>

      <FeedInfoPopup
        feed={infoFeedId ? feedById.get(infoFeedId) : undefined}
        onClose={() => setInfoFeedId(null)}
      />

      <NewsletterInfoPopup
        show={!!newsletterInfoId}
        email={(newsletterInfoId ? feedById.get(newsletterInfoId)?.url ?? '' : '').replace(/^mailto:/i, '')}
        feedTitle={newsletterInfoId ? feedById.get(newsletterInfoId)?.title : undefined}
        closeFocus={newsletterInfoId ? `sbNlBadge-${newsletterInfoId}` : undefined}
        onClose={() => setNewsletterInfoId(null)}
      />

      <FeedIssuePopup
        show={!!issueFeedId}
        fetchErrorCount={issueFeedId ? feedById.get(issueFeedId)?.fetchErrorCount : undefined}
        fetchFailingSinceAt={issueFeedId ? feedById.get(issueFeedId)?.fetchFailingSinceAt : undefined}
        feedTitle={issueFeedId ? feedById.get(issueFeedId)?.shortTitle || feedById.get(issueFeedId)?.title : undefined}
        closeFocus={issueFeedId ? `sbIssueBadge-${issueFeedId}` : undefined}
        onClose={() => setIssueFeedId(null)}
      />

      {(() => {
        const rawFeed = renameFeedId ? appState.feedEngine.getRawFeed(renameFeedId) : undefined;
        const originalName = rawFeed ? (rawFeed.shortTitle || rawFeed.title) : '';
        const currentCustomName = renameFeedId ? preferences.customFeedNames?.[renameFeedId] : undefined;
        return (
          <RenameFeedPopup
            show={!!renameFeedId}
            originalName={originalName}
            currentCustomName={currentCustomName}
            onClose={() => setRenameFeedId(null)}
            onSave={(name) => {
              if (!renameFeedId) return;
              const next = { ...(preferences.customFeedNames ?? {}), [renameFeedId]: name };
              updatePreferences({ customFeedNames: next });
              setRenameFeedId(null);
            }}
            onReset={() => {
              if (!renameFeedId) return;
              const next = { ...(preferences.customFeedNames ?? {}) };
              delete next[renameFeedId];
              updatePreferences({ customFeedNames: Object.keys(next).length ? next : undefined });
              setRenameFeedId(null);
            }}
          />
        );
      })()}

      {(() => {
        const userPlan = appState.state.currentUser?.userPlan;
        const privateCount = feeds.filter(f => f.feedStatus == 'PRIVATE').length;
        const maxPrivate = userPlan ? getPlanLimits(userPlan).maxPrivateFeeds : getPlanLimits('free').maxPrivateFeeds;
        const remaining = maxPrivate - privateCount;
        return (
          <Popup show={privateFeedInfoOpen} title="Private Feed" onClose={() => setPrivateFeedInfoOpen(false)} closable minWidth="300px">
            <div className="privateFeedInfoPopup">
              <p>This is a <strong>private feed</strong>. Others cannot see or access it unless they add the exact same URL. It will not appear in any public listings.</p>
              <p>You are using <strong>{privateCount} of {maxPrivate}</strong> private feed{maxPrivate != 1 ? 's' : ''} on your plan{remaining > 0 ? `, with ${remaining} remaining` : ' (limit reached)'}.</p>
              <p>Private feeds are intended for paid subscriptions and personalized feeds, such as content behind a paywall or feeds containing matches for a search term.</p>
            </div>
          </Popup>
        );
      })()}

      {sharePopup}
      <ShowMeWhereGuide guide={showAllWhereGuide} />

      <DeleteFolderModal
        show={!!deleteFolderModal}
        folderName={deleteFolderModal?.folderName ?? ''}
        feedCount={deleteFolderModal?.feedCount ?? 0}
        onDeleteFeeds={() => handleDeleteFolderConfirm(true)}
        onKeepFeeds={() => handleDeleteFolderConfirm(false)}
        onCancel={() => setDeleteFolderModal(null)}
      />
    </div>
  );
};

export default LeftSidebar;
